import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { ForwardRefHandlers, ModalLayout } from '../ModalLayout';
import { FullScreenModalLayout } from '../FullscreenModalLayout';
import EdgeButton from '@/components/_buttons/EdgeButton';
import Input from '@/components/_inputs/Input';
import { useForm } from 'react-hook-form';
import { useSessionUser } from '@/hooks/useSessionUser';
import { toast } from 'react-toastify';
import SignInButton from '@/components/SignInButton';

interface Props {
  onClose: () => void;
}

export default function InitAppModal({ onClose }: Props) {
  const { t } = useTranslation();
  const { updateUserName } = useSessionUser();

  const { register, handleSubmit } = useForm<{ userName: string }>({
    defaultValues: {
      userName: '',
    },
  });

  const [isNoCollaborateSteam, setIsNoCollaborateSteam] = React.useState(false);

  const handleClickNoCollaborateSteam = () => {
    setIsNoCollaborateSteam(true);
  };

  const ref = React.useRef<ForwardRefHandlers>(null);

  const updateUser = async (data: { userName: string }) => {
    try {
      await updateUserName(data.userName);
      handleClose();
    } catch (error) {
      toast.error(t('errors.changeUserNameError'));
    }
  };

  const handleClose = React.useCallback(() => {
    ref.current?.close();
  }, []);

  return (
    <FullScreenModalLayout ref={ref} hideCloseButton onClose={onClose}>
      {isNoCollaborateSteam ? (
        <form
          className="h-full flex items-center justify-center"
          onSubmit={handleSubmit(updateUser)}
        >
          <div>
            <div className="p-2 pt-0 mb-4">
              <label className="text-left font-bold mb-1 text-brown-primary block text-base custom-label">
                {t('createUser.nickname')}
              </label>
              <Input
                className="text-base w-full h-[56px] custom-input"
                spellCheck={false}
                placeholder={t('createUser.nicknamePlaceholder')}
                autoComplete="false"
                {...register('userName', { required: true, maxLength: 255 })}
              />
            </div>
            <div className="flex justify-center">
              <EdgeButton
                className="!w-[120px] h-[60px]"
                type={'submit'}
                style={{ backgroundSize: '100% 100%' }}
              >
                {t('register')}
              </EdgeButton>
            </div>
          </div>
        </form>
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 h-full text-2xl text-white">
          <div>{t('initAppModal.title')}</div>
          <div className="text-lg px-8 text-center">
            {t('initAppModal.description')} <br />
            {t('initAppModal.description2')}
          </div>
          <div className="flex flex-col justify-center gap-4">
            <div className="flex flex-col gap-4">
              <a
                href={`${process.env.NEXT_PUBLIC_APP_HOST}/connect/apple`}
                onClick={() => {
                  localStorage.setItem('launched', 'true');
                }}
              >
                <SignInButton provider="apple" />
              </a>
              <a href={`${process.env.NEXT_PUBLIC_APP_HOST}/connect/steam`}>
                <SignInButton provider="steam" />
              </a>
              <SignInButton
                provider="start"
                onClick={handleClickNoCollaborateSteam}
              />
            </div>
          </div>
        </div>
      )}
    </FullScreenModalLayout>
  );
}
